import { useCallback, useEffect, useMemo, useState } from "react"
import { Link, router } from "@inertiajs/react"
import { useInertiaCable } from "@inertia-cable/react"
import { Play, RefreshCw } from "lucide-react"
import { toast } from "sonner"
import { AppShell } from "@/components/layout/app-shell"
import { PageHeader } from "@/components/layout/page-header"
import { ScenarioList } from "@/components/domain/scenario-list"
import { ScenarioDetail } from "@/components/domain/scenario-detail"
import { Button } from "@/components/ui/button"
import { Select, type SelectOption } from "@/components/ui/select"
import type { TestLabAgentOption, TestLabBatchSummary, TestLabScenario } from "@/types/test_lab"

const TEST_PROGRESS_POLL_MS = 5000
const ACTIVE_PROGRESS_STATES = new Set(["queued", "pending", "running"])

type Props = {
  agents: TestLabAgentOption[]
  selectedAgentId: number | null
  selectedScenarioId?: number | null
  scenarios: TestLabScenario[]
  test_runs: TestLabBatchSummary[]
  testing_stream: string
}

export default function IntegrationTesting({
  agents,
  selectedAgentId,
  selectedScenarioId: requestedScenarioId = null,
  scenarios,
  test_runs,
  testing_stream,
}: Props) {
  const initialSelectedScenarioId =
    requestedScenarioId != null && scenarios.some((s) => s.id === requestedScenarioId)
      ? requestedScenarioId
      : scenarios[0]?.id ?? null
  const [selectedScenarioId, setSelectedScenarioId] = useState<number | null>(initialSelectedScenarioId)
  const [busyScenarioIds, setBusyScenarioIds] = useState<Set<number>>(new Set())
  const [runAllBusy, setRunAllBusy] = useState(false)

  const selectedScenario = useMemo(
    () => scenarios.find((s) => s.id === selectedScenarioId) ?? null,
    [scenarios, selectedScenarioId],
  )
  const runDetailPath = useCallback((runId: number) => `/testing/test_runs/${runId}`, [])
  const discussPayloadPath = useCallback((runId: number) => `/testing/test_runs/${runId}/discuss_payload`, [])
  const scenarioUpdatePath = useCallback((scenarioId: number) => `/testing/scenarios/${scenarioId}`, [])

  useEffect(() => {
    if (selectedScenarioId && !scenarios.find((s) => s.id === selectedScenarioId)) {
      setSelectedScenarioId(scenarios[0]?.id ?? null)
    }
  }, [scenarios, selectedScenarioId])

  function selectScenario(scenarioId: number) {
    setSelectedScenarioId(scenarioId)
    if (typeof window === "undefined") return

    const params = new URLSearchParams(window.location.search)
    if (selectedAgentId != null) params.set("agent_id", String(selectedAgentId))
    params.set("scenario_id", String(scenarioId))
    params.delete("run_id")
    window.history.replaceState(window.history.state, "", `/testing/integration?${params.toString()}`)
  }

  const progressPollActive = useMemo(() => {
    if (runAllBusy || busyScenarioIds.size > 0) return true
    if (test_runs.some((batch) => batch.running > 0)) return true
    return scenarios.some((scenario) => ACTIVE_PROGRESS_STATES.has(scenario.latestRunStatus ?? ""))
  }, [busyScenarioIds.size, runAllBusy, scenarios, test_runs])

  useInertiaCable(testing_stream, {
    only: ["scenarios", "test_runs"],
    debounce: 500,
  })

  useEffect(() => {
    if (!progressPollActive) return

    let inFlight = false
    const poll = () => {
      if (inFlight) return
      inFlight = true
      router.reload({
        only: ["scenarios", "test_runs"],
        onSuccess: () => {
          setBusyScenarioIds(new Set())
          setRunAllBusy(false)
        },
        onFinish: () => {
          inFlight = false
        },
      })
    }

    const id = window.setInterval(poll, TEST_PROGRESS_POLL_MS)
    return () => window.clearInterval(id)
  }, [progressPollActive])

  function switchAgent(agentId: number) {
    router.get("/testing/integration", { agent_id: agentId }, { preserveScroll: false, preserveState: false })
  }

  async function csrfPost(path: string, body?: object) {
    const csrf = (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ?? ""
    return fetch(path, {
      method: "POST",
      credentials: "same-origin",
      headers: { "Content-Type": "application/json", Accept: "application/json", "X-CSRF-Token": csrf },
      body: body ? JSON.stringify(body) : undefined,
    })
  }

  async function runScenario(scenarioId: number) {
    setBusyScenarioIds((prev) => new Set(prev).add(scenarioId))
    try {
      const res = await csrfPost(`/testing/scenarios/${scenarioId}/run`, { agent_id: selectedAgentId })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      router.reload({
        only: ["scenarios", "test_runs"],
        onSuccess: () => setBusyScenarioIds(new Set()),
      })
    } catch {
      toast.error("Couldn't start integration scenario")
      setBusyScenarioIds((prev) => {
        const next = new Set(prev)
        next.delete(scenarioId)
        return next
      })
    }
  }

  async function runAll() {
    if (scenarios.length === 0) return
    setRunAllBusy(true)
    try {
      const res = await csrfPost("/testing/integration/test_runs", { agent_id: selectedAgentId })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      router.reload({
        only: ["scenarios", "test_runs"],
        onSuccess: () => setRunAllBusy(false),
      })
    } catch {
      toast.error("Couldn't start integration suite")
      setRunAllBusy(false)
    }
  }

  const agentOptions: SelectOption<number>[] = agents.map((a) => ({ value: a.id, label: a.name }))
  const selectedAgentOption = agentOptions.find((o) => o.value === selectedAgentId) ?? null

  if (agents.length === 0) {
    return (
      <AppShell>
        <PageHeader title="Integration Testing" subtitle="No agents yet — create one to start testing." />
      </AppShell>
    )
  }

  return (
    <AppShell fullBleed>
      <div className="min-h-screen bg-app-bg text-app-text">
        <PageHeader
          title="Integration Testing"
          subtitle="Run full-agent scenarios across routing, skills, tools, actions, and final outcomes."
          actions={
            <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:items-center">
              <Button
                variant="red"
                size="sm"
                onClick={runAll}
                disabled={runAllBusy || scenarios.length === 0}
                className="w-full sm:w-auto"
              >
                <Play size={14} className="mr-1" />
                {runAllBusy ? "Starting..." : "Run integration suite"}
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => router.reload({ only: ["scenarios", "test_runs"] })}
                className="w-full sm:w-auto"
              >
                <RefreshCw size={14} className="mr-1" />
                Refresh
              </Button>
              <div className="flex w-full items-center gap-2 sm:w-auto">
                <label className="hidden text-[11px] uppercase tracking-wide text-app-muted sm:inline">
                  Agent
                </label>
                <div className="w-full min-w-[220px] sm:w-[260px]">
                  <Select<number>
                    value={selectedAgentOption}
                    options={agentOptions}
                    onChange={(opt) => opt && switchAgent(opt.value)}
                    isSearchable={agents.length > 6}
                    placeholder="Select agent..."
                    aria-label="Select agent"
                  />
                </div>
              </div>
            </div>
          }
        />

        <div className="flex min-h-[calc(100vh-7rem)] flex-col gap-4 p-3 sm:p-4 lg:p-5">
          <div className="flex items-center justify-between rounded-card border border-border bg-app-panel/70 px-4 py-3 text-sm">
            <div className="text-app-muted">
              Full-agent catalog is visible in these runs. Use the skill Test Lab for isolated workflow checks.
            </div>
            <Link href="/testing" className="text-brand-red transition hover:text-brand-redLight">
              Back to dashboard
            </Link>
          </div>

          <section className="grid min-h-0 flex-1 gap-4 lg:grid-cols-[minmax(384px,520px)_minmax(0,1fr)]">
            <ScenarioList
              scenarios={scenarios}
              selectedId={selectedScenarioId}
              onSelect={selectScenario}
              onRunOne={runScenario}
              onRunAll={runAll}
              busyScenarioIds={busyScenarioIds}
              runAllBusy={runAllBusy}
              latestBatch={test_runs[0] ?? null}
              title="Integration scenarios"
              description="Replay full-agent routing and cross-skill behavior."
              emptyMessage="No integration scenarios for this agent yet."
            />
            <div className="min-h-[620px] lg:min-h-0">
              {selectedScenario ? (
                <ScenarioDetail
                  scenario={selectedScenario}
                  failurePaneMode="chat"
                  runDetailPath={runDetailPath}
                  discussPayloadPath={discussPayloadPath}
                  scenarioUpdatePath={scenarioUpdatePath}
                  onFix={() => Promise.resolve()}
                />
              ) : (
                <div className="valet-panel flex h-full min-h-[360px] items-center justify-center rounded-card p-6 text-center text-sm text-app-muted">
                  {scenarios.length === 0
                    ? "No integration scenarios for this agent yet."
                    : "Select a scenario from the left to see the conversation and assertions."}
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </AppShell>
  )
}
